import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import {
  Box,
  type SecondaryMenuType,
  SideMenuLayout,
  SideMenuLayoutSecondaryMenuProvider,
} from '@enechain/ecloud-designsystem'

import { BottomMenu } from '~/components/layouts/MainLayout/BottomMenu'
import { Logo } from '~/components/layouts/MainLayout/Logo'
import { SecondaryMenuContent } from '~/components/layouts/MainLayout/SecondaryMenuContent'
import { SideMenuDotsProvider } from '~/components/layouts/MainLayout/SideMenuDotsProvider'
import {
  TICKER_BAR_HEIGHT,
  TickerBar,
} from '~/components/layouts/MainLayout/TickerBar'
import { TopMenu } from '~/components/layouts/MainLayout/TopMenu'
import {
  hasSubMenu,
  menuGroups,
  toSubMenuItems,
} from '~/components/layouts/MainLayout/menuGroups'
import { ProtectedAuthGuard } from '~/components/layouts/ProtectedAuthGuard'
import { useDocumentTitle } from '~/hooks/useDocumentTitle'

const MainLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  useDocumentTitle()

  const secondaryMenu: SecondaryMenuType = useMemo(() => {
    const record: SecondaryMenuType = {}
    for (const group of menuGroups) {
      // 単一リンクの group (= システム管理、 #9228) は副パネルを持たない。
      // 空の panel を登録すると、 配下の画面へ遷移した時に中身の無い副パネルが開く。
      if (!hasSubMenu(group)) continue
      const items = toSubMenuItems(group, t)
      record[group.prefix] = {
        title: t(group.labelKey),
        menu: <SecondaryMenuContent items={items} />,
      }
    }
    return record
  }, [t])

  const renderLogo = useCallback(
    ({ collapsed }: { collapsed: boolean }) => <Logo collapsed={collapsed} />,
    [],
  )

  return (
    // design (`design/js/include-nav.js`) はテロップバーを page 最上部 (= sidebar +
    // main-area の親) に置き、画面横いっぱいに描く。DS の SideMenuLayout は desktop 時
    // に root を `position: fixed; inset: 0` (+ `height: 100%`) で敷くため、単に上へ
    // 兄弟要素を置くと viewport 全面に覆われてバーが隠れる。
    //
    // そこで DS root の `top` / `height` だけを CSS で上書きし、レイアウト領域を
    // テロップバーの下 (= 残り高さ) から始める。wrapper に `transform` 等を掛けて
    // 包含ブロックを作る手もあるが、それは**配下すべての `position: fixed` の基準**を
    // 書き換えてしまい、全画面を覆う前提のインライン fixed バックドロップ
    // (例: `pages/SupplyGap/components/CsvDownloadModal.tsx` の modal backdrop) が
    // バー下領域にしか掛からなくなるため採らない。
    //
    // 上書き先は wrapper 直下の DS root。DS は Fragment で mobile 分岐 (静的配置) と
    // desktop 分岐 (fixed) を並べるが、mobile 分岐は `position: static` なので `top` は
    // 効かず、`height` も hamburger のみの分岐で実害が無い。
    //
    // セレクタを `&&` にしてあるのは、DS root 自身の class (詳細度 0-1-0) と
    // `& > *` (同 0-1-0) が同点になり、勝敗が CSS の挿入順という不定要素に委ねられる
    // のを避けるため (`&&` = wrapper class の 2 重指定で 0-2-0)。
    <>
      <TickerBar />
      <Box
        css={{
          '&& > *': {
            top: TICKER_BAR_HEIGHT,
            height: `calc(100vh - ${TICKER_BAR_HEIGHT})`,
          },
        }}
      >
        {/* ドットの取得 (observer) は layout の 1 か所。副パネル・popup・画面側は context で読む */}
        <SideMenuDotsProvider>
          <SideMenuLayoutSecondaryMenuProvider secondaryMenu={secondaryMenu}>
            <SideMenuLayout
              bottomMenu={<BottomMenu />}
              // card 群 (= Surface.primary = 白) との明度差で master-detail / card 境界を
              // 出すための「面の色」として content 全体に gray を敷く (eclair Surface.secondary)。
              // v3 で prop 名が contentBgColor → contentBackgroundColor に変更。未指定だと
              // SideMenuLayout 既定の Surface.primary (白) になり境界が消える (= v2 からの退行)。
              contentBackgroundColor="Surface.secondary"
              logo={renderLogo}
              topMenu={<TopMenu />}
            >
              <Outlet />
            </SideMenuLayout>
          </SideMenuLayoutSecondaryMenuProvider>
        </SideMenuDotsProvider>
      </Box>
    </>
  )
}

export const ProtectedMainLayout = ProtectedAuthGuard(MainLayout)
